'use client';

import { createSelectListCollection, Select } from '../../components/select';
import { Field } from '../../components/field';
import { Tick01Icon, ArrowUpDownIcon } from '@terra/ui-icons';
import { useMemo } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import {
  TestingInstructionApplyModeSchema,
  TestingInstructionPipelineSchema,
} from '@terra/shared-schemas-orchestrator';
import type { TestingInstructionFormValues } from './testing-instruction-form.schema';
import type { TestingInstructionPipeline } from './types';
import { PipelineMultiSelect } from './PipelineMultiSelect';

type ApplyModeFieldsProps = {
  control: Control<TestingInstructionFormValues>;
  applyMode: TestingInstructionFormValues['applyMode'];
  availablePipelines: TestingInstructionPipeline[];
  showPipelines: boolean;
};

export const ApplyModeFields = ({
  control,
  applyMode,
  availablePipelines,
  showPipelines,
}: ApplyModeFieldsProps) => {
  const t = useTranslations('designSystem.testingInstructions');
  const applyModeOptions = useMemo(
    () =>
      createSelectListCollection({
        items: TestingInstructionApplyModeSchema.options.map((mode) => ({
          label: t(`applyModes.${mode}`),
          value: mode,
        })),
      }),
    [t],
  );
  const pipelineOptions = useMemo(
    () =>
      availablePipelines.map((pipeline) => ({
        label: t(`pipelines.${pipeline}`),
        value: pipeline,
      })),
    [t, availablePipelines],
  );

  return (
    <>
      <Field.Root w="full">
        <Field.Label>{t('form.applyMode')}</Field.Label>
        <Controller
          control={control}
          name="applyMode"
          render={({ field }) => (
            <Select.Root
              collection={applyModeOptions}
              value={field.value ? [field.value] : ['ALWAYS']}
              onValueChange={(details) => {
                const next = TestingInstructionApplyModeSchema.parse(
                  details.value[0] ?? 'ALWAYS',
                );

                field.onChange(next);
              }}
              size="sm"
              positioning={{ sameWidth: true }}
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                  <ArrowUpDownIcon />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  {applyModeOptions.items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          )}
        />
        <Field.HelperText>
          {t(`applyModeHelpers.${applyMode}`)}
        </Field.HelperText>
      </Field.Root>

      {showPipelines ? (
        <Controller
          control={control}
          name="pipelines"
          render={({ field }) => (
            <PipelineMultiSelect
              label={t('form.pipelines')}
              helperText={t('form.pipelinesHelper')}
              summary={
                field.value.length === 0 ||
                field.value.length === availablePipelines.length
                  ? t('appliesTo.allPipelines')
                  : field.value
                      .map((pipeline) => t(`pipelines.${pipeline}`))
                      .join(', ')
              }
              options={pipelineOptions}
              value={field.value}
              onChange={field.onChange}
              parseValue={(value) => {
                const parsed =
                  TestingInstructionPipelineSchema.safeParse(value);

                return parsed.success ? parsed.data : null;
              }}
            />
          )}
        />
      ) : null}
    </>
  );
};
